import React from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { theme } from "../../theme/colors";
import {
  EVENT_TYPE_PICKER_ACCESSIBILITY_LABEL,
  filterCanonicalEventTypes,
  getEventTypeDescription,
  getEventTypeIcon,
  getEventTypeTileLabel,
  resolveEventTypeChipSelection,
} from "../../utils/eventTypeConfig";

export default function EventTypeChipBar({
  eventTypes = [],
  value,
  onChange,
  loading = false,
  disabled = false,
}) {
  const visibleTypes = filterCanonicalEventTypes(eventTypes);
  const displayValue = resolveEventTypeChipSelection(eventTypes, visibleTypes, value);
  const selectedType = visibleTypes.find((t) => String(t.id) === String(displayValue));
  const selectedDescription = selectedType
    ? getEventTypeDescription(selectedType.name)
    : "";

  if (loading) {
    return (
      <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1.5, py: 1 }}>
        <CircularProgress size={20} sx={{ color: theme.primary }} />
        <Typography sx={{ fontSize: 14, color: "#64748b" }}>Loading event types...</Typography>
      </Box>
    );
  }

  if (!visibleTypes.length) {
    return null;
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        role="radiogroup"
        aria-label={EVENT_TYPE_PICKER_ACCESSIBILITY_LABEL}
        sx={{
          display: "grid",
          gridTemplateColumns: `repeat(${visibleTypes.length}, minmax(0, 1fr))`,
          gap: 1,
          width: "100%",
        }}
      >
        {visibleTypes.map((type) => {
          const selected = String(displayValue) === String(type.id);
          const icon = getEventTypeIcon(type.name);
          const tileLabel = getEventTypeTileLabel(type.name, type.display_name);

          return (
            <Box
              key={type.id}
              role="radio"
              aria-checked={selected}
              component="button"
              type="button"
              disabled={disabled}
              onClick={() => !disabled && onChange(String(type.id))}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
                width: "100%",
                minHeight: 52,
                px: 0.75,
                py: 0.75,
                borderRadius: "10px",
                border: `1px solid ${theme.primary}`,
                bgcolor: selected ? theme.primary : theme.background,
                color: selected ? theme.background : theme.primary,
                cursor: disabled ? "default" : "pointer",
                opacity: disabled ? 0.7 : 1,
                "&:hover": disabled
                  ? {}
                  : {
                      bgcolor: selected ? theme.primaryHover : theme.background,
                      borderColor: theme.primaryHover,
                      color: selected ? theme.background : theme.primaryHover,
                    },
                "&:focus-visible": {
                  boxShadow: theme.joinFocusRing,
                  outline: "none",
                },
              }}
            >
              {icon && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    color: "inherit",
                    "& svg": { fontSize: 18 },
                    flexShrink: 0,
                  }}
                >
                  {icon}
                </Box>
              )}
              <Typography
                component="span"
                sx={{
                  fontSize: 12,
                  fontWeight: selected ? 700 : 600,
                  lineHeight: 1.3,
                  color: "inherit",
                  textAlign: "center",
                }}
              >
                {tileLabel}
              </Typography>
            </Box>
          );
        })}
      </Box>
      {selectedDescription && (
        <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#334155", mt: 1.25, lineHeight: 1.5 }}>
          {selectedDescription}
        </Typography>
      )}
    </Box>
  );
}
